import ConfirmDialog from '@components/Common/ConfirmDialog';
import { StageStepper } from '@components/Common/EntityWorkspace';
import SelectFormField from '@components/Common/SelectFormField';
import WorkspaceInput from '@components/Common/WorkspaceInput';
import {
  CLOSED_REASONS,
  detailsFor,
  isTerminalStage,
  requirementOf,
  resolveStatus,
  stageDotColor,
  stagesFor,
} from '@constants/applicationStatus';
import { jobApplicationStatusUpdate, reopenJobApplicationMutation } from '@constants/graphQLQueries';
import { fetchGraphQLApiData } from '@helpers/graphQLApi';
import { Box, Button } from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { nextStage, stageProgress } from './stageProgress';

export interface StatusCardApp {
  id: string;
  status: string;
  subStatus?: string | null;
  closedReason?: string | null;
  closedNote?: string | null;
  job?: { hiringMode?: string; requirementStatus?: string | null; jobStatus?: string | null };
  history?: Array<{ status: string; previousStatus?: string | null }>;
}

interface Draft {
  stage: string;
  detail: string;
  reason: string;
  closedNote: string;
  note: string;
}

const NONE = '';

/** The backend writes its refusals for this screen, so they are shown as they come. */
export const refusalText = (e: unknown, fallback: string): string =>
  String((e as { message?: string })?.message ?? '').replace(/^GraphQL errors:\s*/, '').trim() || fallback;

/**
 * Where the candidate is, and the one control that moves them.
 *
 * Stage, detail and closed reason are saved together because the backend judges them together
 * (a detail must belong to its stage; Closed needs a reason, and Other a note). The stages offered
 * are the job's own: ten on a managed job, six on a self-serve one. What is offered is only a
 * courtesy; `ApplicationWorkflow` on the backend is what refuses.
 */
const StatusCard: React.FC<{
  app: StatusCardApp;
  /** False when a managed candidate belongs to another recruiter. */
  canMove: boolean;
  superAdmin: boolean;
  /** The consent override, rendered beside the controls when it applies. */
  consentAction?: React.ReactNode;
  /** Consent is outstanding, so the one-click move stops short of Submitted. */
  consentPending?: boolean;
  onChanged: () => Promise<void> | void;
  onError: (message: string) => void;
}> = ({ app, canMove, superAdmin, consentAction, consentPending = false, onChanged, onError }) => {
  const managed = app.job?.hiringMode === 'MANAGED';
  const resolved = resolveStatus(app.status, app.subStatus, app.closedReason);
  const current = resolved.stage?.label ?? '';
  const terminal = isTerminalStage(current);
  const requirement = requirementOf(app.job?.requirementStatus, app.job?.jobStatus);
  const locked = requirement === 'On Hold' || requirement === 'Filled' || requirement === 'Closed';

  const initial = (): Draft => ({
    stage: current,
    detail: resolved.detail && resolved.detail !== current ? resolved.detail : NONE,
    reason: resolved.closedReason,
    closedNote: app.closedNote ?? '',
    note: '',
  });
  const [draft, setDraft] = useState<Draft>(initial);
  const [saving, setSaving] = useState(false);
  const [advancing, setAdvancing] = useState(false);
  const [reopenOpen, setReopenOpen] = useState(false);
  const [reopenStage, setReopenStage] = useState('');

  // A reload (after any save on the page) resets the control to what is stored.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => setDraft(initial()), [app.status, app.subStatus, app.closedReason, app.closedNote]);

  const set = (k: keyof Draft, v: string) =>
    setDraft((d) => (k === 'stage' ? { ...d, stage: v, detail: NONE } : { ...d, [k]: v }));

  const jobStages = stagesFor(managed);
  const stageOptions = useMemo(() => {
    let list = jobStages.map((s) => s.label);
    // A record written before the workflow can sit on a stage its job no longer uses.
    if (current && !list.includes(current)) list = [current, ...list];
    // The job lock: while paused or ended, a candidate can only stay or be closed.
    if (locked && !superAdmin) list = list.filter((s) => s === current || s === 'Closed');
    return list.map((s) => ({ value: s, label: s }));
  }, [jobStages, current, locked, superAdmin]);

  const details = detailsFor(draft.stage);
  const closing = draft.stage === 'Closed';
  const changed =
    draft.stage !== current
    || (draft.detail || NONE) !== (resolved.detail && resolved.detail !== current ? resolved.detail : NONE)
    || (closing && (draft.reason !== resolved.closedReason || draft.closedNote !== (app.closedNote ?? '')));
  const invalid = closing && (!draft.reason || (draft.reason === 'Other' && !draft.closedNote.trim()));

  const save = async () => {
    if (!changed || invalid) return;
    setSaving(true);
    try {
      await fetchGraphQLApiData(
        jobApplicationStatusUpdate(app.id, {
          status: draft.stage,
          subStatus: closing ? undefined : draft.detail || undefined,
          closedReason: closing ? draft.reason : undefined,
          closedNote: closing ? draft.closedNote.trim() || undefined : undefined,
          statusNote: draft.note.trim() || undefined,
        }),
        { showStatus: true, message: 'Status updated' },
      );
      await onChanged();
    } catch (e) {
      onError(refusalText(e, 'Failed to update the status. Please try again.'));
    } finally {
      setSaving(false);
    }
  };

  /*
   * The one-click move. Only the stage is sent, and only when stageProgress.nextStage says the
   * backend would take it bare (no detail, no reason, no gate), so it is the same mutation the
   * form saves, minus the choices that one step forward does not need.
   */
  const advanceTo = nextStage(app.status, { managed, canMove, locked, superAdmin, consentPending });
  const advance = async () => {
    if (!advanceTo) return;
    setAdvancing(true);
    try {
      await fetchGraphQLApiData(jobApplicationStatusUpdate(app.id, { status: advanceTo }), {
        showStatus: true,
        message: `Moved to ${advanceTo}`,
      });
      await onChanged();
    } catch (e) {
      onError(refusalText(e, 'Could not move this candidate. Please try again.'));
    } finally {
      setAdvancing(false);
    }
  };

  const reopen = async (reason?: string) => {
    if (!reopenStage || !reason?.trim()) return;
    try {
      await fetchGraphQLApiData(reopenJobApplicationMutation(app.id, reopenStage, reason.trim()), {
        showStatus: true,
        message: 'Candidate reopened',
      });
      setReopenOpen(false);
      await onChanged();
    } catch (e) {
      onError(refusalText(e, 'Could not reopen this candidate. Please try again.'));
    }
  };

  /*
   * The stepper is the path, not the exits: Closed is drawn as a branch after it, with its reason,
   * and the path stays lit up to where the candidate stopped. Drawing Closed as the last step
   * would mark every earlier stage done for someone rejected at screening.
   */
  const progress = stageProgress(app, managed);

  const disabled = !canMove || terminal || saving;

  return (
    <Box className="cc-stagecard">
      <Box className="cc-stagecard__header">
        <Box>
          <div className="cc-stagecard__title">Candidate status</div>
          <div className="cc-stagecard__sub">
            {managed ? 'Premium Hiring job: all ten stages' : 'Self-serve job: the employer runs this pipeline'}
            {locked ? `. This job is ${requirement.toLowerCase()}, so candidates can be closed but not moved forward.` : ''}
          </div>
        </Box>
        <Box className="cc-status-actions">
          {consentAction}
          {advanceTo && (
            <Button
              size="small"
              variant="contained"
              disabled={advancing || saving || changed}
              onClick={advance}
            >
              {advancing ? 'Moving…' : `Move to ${advanceTo}`}
            </Button>
          )}
          {terminal && superAdmin && (
            <Button size="small" variant="outlined" onClick={() => { setReopenStage(''); setReopenOpen(true); }}>
              Reopen
            </Button>
          )}
        </Box>
      </Box>

      <Box className="cc-status-steps">
        <StageStepper
          scroll
          currentIndex={progress.currentIndex}
          stages={progress.path.map((s) => ({ id: s.label, label: s.label, color: stageDotColor(s.label) }))}
          exit={progress.exit ? {
            label: progress.exit.label,
            detail: progress.exit.reason || undefined,
            color: stageDotColor(progress.exit.label),
            stoppedIndex: progress.exit.stoppedIndex,
          } : null}
        />
      </Box>

      <Box className="cc-status-form">
        {/* The class is on a wrapper, not the field: a disabled SelectFormField renders plain text
            with no FormControl, and the stage-colour value styling and tests both key on it. */}
        <Box className="cc-status-select">
          <SelectFormField
            required={false}
            name="stage"
            label="Stage"
            size="small"
            value={draft.stage}
            options={stageOptions}
            disabled={disabled}
            onChange={(_n, v) => set('stage', String(v))}
            selectProps={{
              renderValue: (selected) => (
                <Box className="cc-status-select__value">
                  <span className="cc-status-select__dot" style={{ background: stageDotColor(String(selected)) }} />
                  {String(selected)}
                </Box>
              ),
            }}
          />
        </Box>
        {details.length > 0 && (
          <SelectFormField
            required={false}
            name="detail"
            label="Detail"
            size="small"
            value={draft.detail}
            options={[{ value: NONE, label: 'None' }, ...details.map((d) => ({ value: d, label: d }))]}
            disabled={disabled}
            onChange={(_n, v) => set('detail', String(v))}
          />
        )}
        {closing && (
          <SelectFormField
            required={false}
            name="closedReason"
            label="Reason"
            size="small"
            value={draft.reason}
            options={CLOSED_REASONS.map((r) => ({ value: r, label: r }))}
            disabled={disabled}
            error={!draft.reason && draft.stage !== current}
            onChange={(_n, v) => set('reason', String(v))}
          />
        )}
        {closing && (
          <Box className="cc-status-form__wide">
            <WorkspaceInput
              value={draft.closedNote}
              onChange={(v) => set('closedNote', v)}
              placeholder={draft.reason === 'Other' ? 'Explain the reason (required)' : 'Anything worth keeping about why (optional)'}
              error={draft.reason === 'Other' && !draft.closedNote.trim()}
              disabled={disabled}
            />
          </Box>
        )}
        {changed && (
          <Box className="cc-status-form__wide">
            <WorkspaceInput
              value={draft.note}
              onChange={(v) => set('note', v)}
              placeholder="Note for the history (optional)"
              disabled={disabled}
            />
          </Box>
        )}
        {changed && (
          <Box className="cc-status-form__actions">
            <Button size="small" onClick={() => setDraft(initial())} disabled={saving}>Cancel</Button>
            <Button size="small" variant="contained" onClick={save} disabled={saving || invalid}>
              {saving ? 'Saving…' : 'Save status'}
            </Button>
          </Box>
        )}
      </Box>

      {/* A disabled control with no reason reads as a broken page. Say why. */}
      {!canMove && (
        <span className="cc-status-owned">
          Managed by another recruiter. You can view this pipeline; only the candidate&apos;s recruiter can change
          their status.
        </span>
      )}
      {terminal && canMove && !superAdmin && (
        <span className="cc-status-owned">
          This candidate is {current.toLowerCase()}. Only a Super Admin can reopen them.
        </span>
      )}

      <ConfirmDialog
        open={reopenOpen}
        title="Reopen this candidate?"
        body={(
          <>
            <p>
              They go back to the stage you choose, and the reason is kept in their history.
            </p>
            <SelectFormField
              required={false}
              name="reopenStage"
              label="Move to"
              value={reopenStage}
              options={jobStages.filter((s) => !isTerminalStage(s.label)).map((s) => ({ value: s.label, label: s.label }))}
              onChange={(_n, v) => setReopenStage(String(v))}
            />
          </>
        )}
        actionLabel="Reopen"
        reasonLabel="Why reopen?"
        reasonPlaceholder="For example: the client asked to see them again"
        onCancel={() => setReopenOpen(false)}
        onConfirm={reopen}
      />
    </Box>
  );
};

export default StatusCard;
